import { createContext, useEffect, useMemo, useState } from "react";
import {
  addOfflineProgressAndGetDiff,
  buyAllSpeedShopUpgrades,
  buyAllUntil10,
  generateProducts,
} from "../lib/calculator";
import React from "react";
import {
  BuySetting,
  Game,
  OfflineDiff,
  createProducer,
  psStartMultiplier,
  startParticleValue,
} from "../types/game";
import { saveGame, loadGame } from "../save/saveCombiner";
import { getUid } from "../auth/user";
import { loadObjectFromLocalStorage } from "../save/localSave";
import { SnackbarContext } from "./SnackbarProvider";
import {
  isAchievementUnlocked,
  unlockAchievement,
} from "../types/achievements";
import Decimal from "break_infinity.js";

const producer0 = createProducer({
  layerLevel: 0,
  unlocked: true,
  costScaling: 10,
  baseCost: 0,
  value: new Decimal(startParticleValue),
});
const producer1 = createProducer({
  layerLevel: 1,
  unlocked: true,
  costScaling: 10,
  baseCost: 10,
});
const producer2 = createProducer({
  layerLevel: 2,
  unlocked: true,
  costScaling: 20,
  baseCost: 1000,
});
const producer3 = createProducer({
  layerLevel: 3,
  unlocked: true,
  costScaling: 50,
  baseCost: 1e6,
});
const producer4 = createProducer({
  layerLevel: 4,
  unlocked: true,
  costScaling: 100,
  baseCost: 1e9,
});
const producer5 = createProducer({
  layerLevel: 5,
  unlocked: false,
  costScaling: 200,
  baseCost: 1e12,
});
const producer6 = createProducer({
  layerLevel: 6,
  unlocked: false,
  costScaling: 500,
  baseCost: 1e15,
});
const producer7 = createProducer({
  layerLevel: 7,
  unlocked: false,
  costScaling: 1000,
  baseCost: 1e18,
});
const producer8 = createProducer({
  layerLevel: 8,
  unlocked: false,
  costScaling: 2000,
  baseCost: 1e21,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const producer9 = createProducer({
  layerLevel: 9,
  unlocked: false,
  costScaling: 5_000,
  baseCost: 1e24,
});
export const newGame: Game = {
  producers: [
    producer0,
    producer1,
    producer2,
    producer3,
    producer4,
    producer5,
    producer6,
    producer7,
    producer8,
    //producer9,
  ],
  producerStrengthening: {
    upgradeLevel: 0,
    multiplier: psStartMultiplier,
  },
  strengtheningBoost: { upgradeLevel: 0, alreadySeen: false },
  unlockedAchievementIds: new Set(),
  molecule: {
    value: new Decimal(0),
    multiplier: 1,
    unlocked: false,
    graphNodes: {},
    creations: 0,
    startedAt: Date.now(),
  },
  producerAutoBuyTime: 5000,
  createdAt: Date.now(),
  last10MoleculeCreations: [],
  neutronState: {
    timeMultiplier: 1,
    multiplierMultiplier: 1,
    state: "Neutron",
  },
};

export const GameContext = createContext({
  buySetting: BuySetting.BuyOne as BuySetting,
  setBuySetting: {} as React.Dispatch<React.SetStateAction<BuySetting>>,
  achievementMultiplier: 1,
  timeProgressArray: [] as number[],
  setTimeProgressArray: [] as React.Dispatch<React.SetStateAction<number>>[],
  autoSave: true,
  setAutoSave: {} as React.Dispatch<React.SetStateAction<boolean>>,
  offlineDiff: undefined as OfflineDiff | undefined,
  setOfflineDiff: {} as React.Dispatch<
    React.SetStateAction<OfflineDiff | undefined>
  >,
  offlineDialogOpen: false,
  setOfflineDialogOpen: {} as React.Dispatch<React.SetStateAction<boolean>>,
  game: newGame as Game,
  setGame: {} as React.Dispatch<React.SetStateAction<Game>>,
  manualAutoCollect: false,
  setManualAutoCollect: {} as React.Dispatch<React.SetStateAction<boolean>>,
});

export const GameProvider = ({ children }: { children: any }) => {
  const { showSnackbar } = React.useContext(SnackbarContext);
  const [offlineDiff, setOfflineDiff] = useState<OfflineDiff | undefined>(
    undefined
  );
  const [offlineDialogOpen, setOfflineDialogOpen] = React.useState(false);
  const [manualAutoCollect, setManualAutoCollect] = useState(false);

  const [autoSave, setAutoSave] = useState(
    loadObjectFromLocalStorage("autoSave") ?? true
  );
  const uid = getUid();
  const [game, setGame] = useState<Game>(newGame);

  const achievementMultiplier = useMemo(() => {
    if (!game || !game.unlockedAchievementIds) return 1;
    const collectThemAllUnlocked = game.unlockedAchievementIds.has(11);
    const divider = collectThemAllUnlocked ? 7.5 : 15;
    return 1 + game.unlockedAchievementIds.size / divider;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.unlockedAchievementIds.size]);

  useEffect(() => {
    if (game.producerStrengthening.upgradeLevel === 4) {
      const newlyUnlocked = unlockAchievement(
        12,
        game.unlockedAchievementIds,
        setGame
      );
      if (newlyUnlocked) {
        showSnackbar("Achievement: S-S-S-Strengthening!");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.producerStrengthening.upgradeLevel, showSnackbar]);

  const [buySetting, setBuySetting] = React.useState<BuySetting>(
    BuySetting.BuyOne
  );

  const [timeProgressProducer0, setTimeProgressProducer0] = React.useState(0);
  const [timeProgressProducer1, setTimeProgressProducer1] = React.useState(0);
  const [timeProgressProducer2, setTimeProgressProducer2] = React.useState(0);
  const [timeProgressProducer3, setTimeProgressProducer3] = React.useState(0);
  const [timeProgressProducer4, setTimeProgressProducer4] = React.useState(0);
  const [timeProgressProducer5, setTimeProgressProducer5] = React.useState(0);
  const [timeProgressProducer6, setTimeProgressProducer6] = React.useState(0);
  const [timeProgressProducer7, setTimeProgressProducer7] = React.useState(0);
  const [timeProgressProducer8, setTimeProgressProducer8] = React.useState(0);

  const timeProgressArray = useMemo(
    () => [
      timeProgressProducer0,
      timeProgressProducer1,
      timeProgressProducer2,
      timeProgressProducer3,
      timeProgressProducer4,
      timeProgressProducer5,
      timeProgressProducer6,
      timeProgressProducer7,
      timeProgressProducer8,
    ],
    [
      timeProgressProducer0,
      timeProgressProducer1,
      timeProgressProducer2,
      timeProgressProducer3,
      timeProgressProducer4,
      timeProgressProducer5,
      timeProgressProducer6,
      timeProgressProducer7,
      timeProgressProducer8,
    ]
  );

  const setTimeProgressArray = useMemo(
    () => [
      setTimeProgressProducer0,
      setTimeProgressProducer1,
      setTimeProgressProducer2,
      setTimeProgressProducer3,
      setTimeProgressProducer4,
      setTimeProgressProducer5,
      setTimeProgressProducer6,
      setTimeProgressProducer7,
      setTimeProgressProducer8,
    ],
    []
  );

  const [oldProducerAutoBuyTimestamp, setOldProducerAutoBuyTimestamp] =
    useState(Date.now() - 50000);
  const [oldSpeedAutoBuyTimestamp, setOldSpeedAutoBuyTimestamp] = useState(
    Date.now() - 20000
  );
  const [lastTickTimestamp, setLastTickTimestamp] = useState(Date.now());

  useEffect(() => {
    const tickspeed = 50;
    const intervalId = setInterval(() => {
      let allCollectable = true;
      const currTimestamp = Date.now();
      const timeDiff = currTimestamp - lastTickTimestamp;
      game.producers.forEach((producer, index) => {
        if (index === 0) return;
        if (
          producer.produceTime * game.neutronState.timeMultiplier >
          timeProgressArray[index]
        ) {
          allCollectable = false;
        }
        if (producer.value.eq(0)) return;

        const setTimeProgress = setTimeProgressArray[index];
        const timeProgress = timeProgressArray[index];
        if (
          timeProgress <
          producer.produceTime * game.neutronState.timeMultiplier
        ) {
          setTimeProgress((prev: number) => {
            const newTimeProgress = prev + timeDiff;
            return newTimeProgress;
          });
        }
        if (
          (producer.automateCollect || manualAutoCollect) &&
          timeProgress >=
            producer.produceTime * game.neutronState.timeMultiplier &&
          !producer.automateCollectDisabled
        ) {
          allCollectable = false;
          setGame((prev: Game) => ({
            ...prev,
            producers: prev.producers.map((prevProducer, prevIndex) => {
              if (prevIndex === index - 1) {
                return {
                  ...prevProducer,
                  value: prevProducer.value.plus(
                    generateProducts(producer, achievementMultiplier, game)
                  ),
                };
              }
              return prevProducer;
            }),
          }));
          setTimeProgress((prev: number) => {
            return 0;
          });
        }
      });
      if (Object.keys(game.molecule.graphNodes).includes("Carbon")) {
        if (
          Date.now() >
          oldProducerAutoBuyTimestamp + game.producerAutoBuyTime
        ) {
          buyAllUntil10(game.producers, setGame, true);
          setOldProducerAutoBuyTimestamp(Date.now());
        }
      }
      if (Object.keys(game.molecule.graphNodes).includes("Chlorine")) {
        if (Date.now() > oldSpeedAutoBuyTimestamp + game.producerAutoBuyTime) {
          buyAllSpeedShopUpgrades(game, setGame);
          setOldSpeedAutoBuyTimestamp(Date.now());
        }
      }
      setTimeProgressArray[0]((prev: number) => prev + 1);
      if (
        allCollectable &&
        game.producers[game.producers.length - 2].unlocked
      ) {
        const newlyUnlocked = unlockAchievement(
          11,
          game.unlockedAchievementIds,
          setGame
        );
        if (newlyUnlocked) {
          showSnackbar("Achievement unlocked: Collect them all!");
        }
      }
      setLastTickTimestamp(currTimestamp);
    }, tickspeed);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeProgressArray, setTimeProgressArray, game]);

  React.useEffect(() => {
    if (game.producers[0].value.lt(0)) {
      setGame((prev) => {
        return {
          ...prev,
          producers: prev.producers.map((producer, index) => {
            if (index === 0) return { ...producer, value: new Decimal(0) };
            return producer;
          }),
        };
      });
    }
  }, [game]);

  React.useEffect(() => {
    const loadGameData = async () => {
      const loadedGame = await loadGame(uid);
      console.log("loadedGame:", loadedGame);
      if (!loadedGame) return;
      const diff = addOfflineProgressAndGetDiff(loadedGame);
      setOfflineDiff(diff);
      setOfflineDialogOpen(true);
      //fix for old save stats
      if (
        !loadedGame.molecule ||
        !loadedGame.molecule.graphNodes ||
        !loadedGame.molecule.creations ||
        !loadedGame.molecule.startedAt
      ) {
        loadedGame.molecule = {
          value: new Decimal(0),
          multiplier: 1,
          unlocked: false,
          graphNodes: {},
          creations: 0,
          startedAt: Date.now(),
        };
      }
      if (!loadedGame.strengtheningBoost.alreadySeen) {
        loadedGame.strengtheningBoost.alreadySeen = true;
      }
      if (!loadedGame.last10MoleculeCreations) {
        loadedGame.last10MoleculeCreations = [];
      }
      if (!loadedGame.neutronState) {
        loadedGame.neutronState = {
          timeMultiplier: 1,
          multiplierMultiplier: 1,
          state: "Neutron",
        };
      }
      if (
        typeof loadedGame.producers[0].multiplier === "number" ||
        typeof loadedGame.producers[0].multiplier === "string"
      ) {
        loadedGame.producers.forEach((producer) => {
          producer.multiplier = new Decimal(producer.multiplier);
        });
        loadedGame.producerStrengthening.multiplier = new Decimal(
          loadedGame.producerStrengthening.multiplier
        );
      }
      console.log("loadedGame:", loadedGame);
      setGame(loadedGame);
    };

    loadGameData();
  }, [uid]);

  const [oldSaveTimeStamp, setOldSaveTimeStamp] = useState(Date.now());
  useEffect(() => {
    if (!autoSave) return;
    const timeStamp = Date.now();
    if (timeStamp >= oldSaveTimeStamp + 300_000) {
      setOldSaveTimeStamp(timeStamp);
      /*       if (!uid) {
        showSnackbar(
          "You are not logged in, so your progress will not be saved"
        );
        return;
      } */
      saveGame(uid, game);
      showSnackbar("Game saved");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, uid]);

  useEffect(() => {
    if (
      game.producers[0].value.greaterThanOrEqualTo(1e55) &&
      !isAchievementUnlocked(13, game.unlockedAchievementIds)
    ) {
      setGame((prev) => {
        return {
          ...prev,
          unlockedAchievementIds: prev.unlockedAchievementIds.add(13),
        };
      });
      showSnackbar("Achievement: Why you should do this?");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);

  return (
    <GameContext.Provider
      value={{
        achievementMultiplier,
        buySetting,
        setBuySetting,
        timeProgressArray,
        setTimeProgressArray,
        setGame,
        autoSave,
        setAutoSave,
        offlineDiff: offlineDiff,
        setOfflineDiff: setOfflineDiff,
        offlineDialogOpen: offlineDialogOpen,
        setOfflineDialogOpen: setOfflineDialogOpen,
        game,
        manualAutoCollect,
        setManualAutoCollect,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};
